/**
 * Node Registry Store
 *
 * Zustand store for workflow node registry.
 * Fetches node definitions from the backend API and caches them.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  ExecutionTarget,
  NodeCategory,
  NodeDefinition,
  NodeDefinitionSummary,
  NodesByCategory,
  CategoryMetadata,
  TargetDefinition,
  DeviceCapabilities,
  NodeValidationResult,
  WorkflowValidationResult,
  NodeRegistryStats,
} from '@/types/workflow-nodes';
import { defaultWorkflowConfig } from '@/types/workflow-config';

interface NodeRegistryState {
  // Data
  nodes: Map<string, NodeDefinition>;
  nodesByCategory: NodesByCategory[];
  categories: CategoryMetadata[];
  targets: TargetDefinition[];
  stats: NodeRegistryStats | null;

  // Filters
  selectedTarget: ExecutionTarget;
  searchQuery: string;
  selectedDevice: DeviceCapabilities | null;

  // Loading states
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;

  // Actions
  initialize: () => Promise<void>;
  setSelectedTarget: (target: ExecutionTarget) => void;
  setSearchQuery: (query: string) => void;
  setSelectedDevice: (device: DeviceCapabilities | null) => void;

  // Getters
  getNode: (type: string) => NodeDefinition | undefined;
  getFilteredNodes: () => NodeDefinitionSummary[];
  getFilteredNodesByCategory: () => NodesByCategory[];
  validateNode: (nodeType: string) => Promise<NodeValidationResult>;
  validateWorkflow: (nodeTypes: string[]) => Promise<WorkflowValidationResult>;
}

const WORKFLOW_API_BASE = defaultWorkflowConfig.workflowEngineUrl;

export const useNodeRegistryStore = create<NodeRegistryState>()(
  devtools(
    (set, get) => ({
      // Initial state
      nodes: new Map(),
      nodesByCategory: [],
      categories: [],
      targets: [],
      stats: null,
      selectedTarget: ExecutionTarget.SERVER,
      searchQuery: '',
      selectedDevice: null,
      isLoading: false,
      isInitialized: false,
      error: null,

      // Initialize - fetch all node data
      initialize: async () => {
        const { isInitialized, isLoading } = get();
        if (isInitialized || isLoading) return;

        set({ isLoading: true, error: null });

        try {
          // Fetch all data in parallel
          const [nodesRes, categoriesRes, targetsRes] = await Promise.all([
            fetch(`${WORKFLOW_API_BASE}/api/nodes?grouped=true`),
            fetch(`${WORKFLOW_API_BASE}/api/nodes/categories`),
            fetch(`${WORKFLOW_API_BASE}/api/nodes/targets`),
          ]);

          if (!nodesRes.ok || !categoriesRes.ok || !targetsRes.ok) {
            throw new Error('Failed to fetch node registry data');
          }

          const [nodesData, categoriesData, targetsData] = await Promise.all([
            nodesRes.json(),
            categoriesRes.json(),
            targetsRes.json(),
          ]);

          // Build nodes map from grouped data
          const nodes = new Map<string, NodeDefinition>();
          const nodesByCategory: NodesByCategory[] = nodesData.data.categories;

          for (const category of nodesByCategory) {
            for (const node of category.nodes) {
              // Fetch full node definition for each node
              // For performance, we store summaries initially
              nodes.set(node.type, node as NodeDefinition);
            }
          }

          set({
            nodes,
            nodesByCategory,
            categories: categoriesData.data.categories,
            targets: targetsData.data.targets,
            stats: nodesData.data.stats,
            isInitialized: true,
            isLoading: false,
          });
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Unknown error';
          set({ error: message, isLoading: false });
          console.error('Failed to initialize node registry:', error);
        }
      },

      // Set selected target
      setSelectedTarget: (target: ExecutionTarget) => {
        set({ selectedTarget: target });
      },

      // Set search query
      setSearchQuery: (query: string) => {
        set({ searchQuery: query });
      },

      // Set selected device (for ESP32 capability filtering)
      setSelectedDevice: (device: DeviceCapabilities | null) => {
        set({ selectedDevice: device });
      },

      // Get a specific node definition
      getNode: (type: string) => {
        return get().nodes.get(type);
      },

      // Get filtered nodes list
      getFilteredNodes: () => {
        const { nodes, selectedTarget, searchQuery, selectedDevice } = get();
        let result: NodeDefinitionSummary[] = [];

        for (const node of nodes.values()) {
          // Filter by target
          if (selectedTarget !== ExecutionTarget.ALL) {
            if (
              !node.targets.includes(selectedTarget) &&
              !node.targets.includes(ExecutionTarget.ALL)
            ) {
              continue;
            }
          }

          // Filter by search query
          if (searchQuery) {
            const query = searchQuery.toLowerCase();
            if (
              !node.name.toLowerCase().includes(query) &&
              !node.description.toLowerCase().includes(query) &&
              !node.type.toLowerCase().includes(query)
            ) {
              continue;
            }
          }

          // Filter by device capabilities (for ESP32)
          if (selectedTarget === ExecutionTarget.ESP32 && selectedDevice && node.capabilities) {
            const deviceCaps = getDeviceCapabilityList(selectedDevice);
            const hasAllCaps = node.capabilities.every(cap => deviceCaps.includes(cap));
            if (!hasAllCaps) {
              continue;
            }
          }

          result.push(node);
        }

        return result;
      },

      // Get filtered nodes grouped by category
      getFilteredNodesByCategory: () => {
        const { nodesByCategory, selectedTarget, searchQuery, selectedDevice } = get();
        const result: NodesByCategory[] = [];

        for (const category of nodesByCategory) {
          const filteredNodes: NodeDefinitionSummary[] = [];

          for (const node of category.nodes) {
            // Filter by target
            if (selectedTarget !== ExecutionTarget.ALL) {
              if (
                !node.targets.includes(selectedTarget) &&
                !node.targets.includes(ExecutionTarget.ALL)
              ) {
                continue;
              }
            }

            // Filter by search query
            if (searchQuery) {
              const query = searchQuery.toLowerCase();
              if (
                !node.name.toLowerCase().includes(query) &&
                !node.description.toLowerCase().includes(query) &&
                !node.type.toLowerCase().includes(query)
              ) {
                continue;
              }
            }

            // Filter by device capabilities (for ESP32)
            if (selectedTarget === ExecutionTarget.ESP32 && selectedDevice && node.capabilities) {
              const deviceCaps = getDeviceCapabilityList(selectedDevice);
              const hasAllCaps = node.capabilities.every(cap => deviceCaps.includes(cap));
              if (!hasAllCaps) {
                continue;
              }
            }

            filteredNodes.push(node);
          }

          if (filteredNodes.length > 0) {
            result.push({
              ...category,
              nodes: filteredNodes,
            });
          }
        }

        return result;
      },

      // Validate a single node for current device
      validateNode: async (nodeType: string) => {
        const { selectedDevice } = get();

        try {
          const res = await fetch(`${WORKFLOW_API_BASE}/api/nodes/validate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              nodeType,
              deviceCapabilities: selectedDevice,
            }),
          });

          if (!res.ok) {
            throw new Error('Validation request failed');
          }

          const data = await res.json();
          return {
            nodeType,
            valid: data.data.valid,
            errors: data.data.missingCapabilities.map(
              (cap: string) => `Missing capability: ${cap}`
            ),
          };
        } catch (error) {
          return {
            nodeType,
            valid: false,
            errors: [error instanceof Error ? error.message : 'Validation failed'],
          };
        }
      },

      // Validate entire workflow
      validateWorkflow: async (nodeTypes: string[]) => {
        const { selectedTarget, selectedDevice } = get();

        try {
          const res = await fetch(`${WORKFLOW_API_BASE}/api/nodes/validate-workflow`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              nodes: nodeTypes,
              target: selectedTarget,
              deviceCapabilities: selectedDevice,
            }),
          });

          if (!res.ok) {
            throw new Error('Workflow validation request failed');
          }

          const data = await res.json();
          return data.data as WorkflowValidationResult;
        } catch (error) {
          return {
            valid: false,
            target: selectedTarget,
            results: nodeTypes.map(type => ({
              nodeType: type,
              valid: false,
              errors: [error instanceof Error ? error.message : 'Validation failed'],
            })),
          };
        }
      },
    }),
    { name: 'node-registry-store' }
  )
);

// Helper function to convert device capabilities to list
function getDeviceCapabilityList(device: DeviceCapabilities): string[] {
  const caps: string[] = [];
  const c = device.capabilities;

  if (c.has_display) caps.push('display');
  if (c.has_touch) caps.push('touch');
  if (c.has_wifi) caps.push('wifi');
  if (c.has_bluetooth) caps.push('bluetooth');
  if (c.has_ethernet) caps.push('ethernet');
  if (c.gpio_count > 0) caps.push('gpio');
  if (c.analog_pins.length > 0) caps.push('adc');
  if (c.pwm_pins.length > 0) caps.push('pwm');
  if (c.dac_pins.length > 0) caps.push('dac');
  caps.push(...c.sensors);

  return caps;
}

// Export category labels for UI
export const categoryLabels: Record<NodeCategory, string> = {
  [NodeCategory.CONTROL]: 'Control',
  [NodeCategory.VARIABLES]: 'Variables',
  [NodeCategory.TIMING]: 'Timing',
  [NodeCategory.INTEGRATIONS]: 'Integrations',
  [NodeCategory.NOTIFICATIONS]: 'Notifications',
  [NodeCategory.DATABASE]: 'Database',
  [NodeCategory.ANALYTICS]: 'Analytics',
  [NodeCategory.HUMAN_WORKFLOW]: 'Human Workflow',
  [NodeCategory.GPIO]: 'GPIO',
  [NodeCategory.SENSORS]: 'Sensors',
  [NodeCategory.COMMUNICATION]: 'Communication',
  [NodeCategory.DISPLAY]: 'Display',
  [NodeCategory.INPUT]: 'Input',
  [NodeCategory.SECURITY]: 'Security',
};

// Export target labels for UI
export const targetLabels: Record<ExecutionTarget, string> = {
  [ExecutionTarget.SERVER]: 'Server',
  [ExecutionTarget.ESP32]: 'ESP32',
  [ExecutionTarget.ALL]: 'All Targets',
};
